import * as React from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';

interface Destination {
  label: string;
  path: string;
  keywords?: string[];
}

const destinations: Destination[] = [
  { label: 'Home', path: '/', keywords: ['dashboard', 'home'] },
  { label: 'Monitoring Portal', path: '/monitor', keywords: ['monitor', 'portal', 'live'] },
  { label: 'Alerts Dashboard', path: '/alerts', keywords: ['alerts', 'events'] },
  { label: 'Sites', path: '/sites', keywords: ['projects', 'locations', 'site'] },
  { label: 'Customers', path: '/customers', keywords: ['customer', 'client', 'orgs'] },
  { label: 'Devices', path: '/devices', keywords: ['hardware', 'cameras', 'speakers'] },
  { label: 'Connections', path: '/connections', keywords: ['external', 'integrations', 'connections'] },
  { label: 'Users', path: '/users', keywords: ['people', 'team', 'admins'] },
  { label: 'Reporting', path: '/reporting', keywords: ['reports', 'analytics'] },
  { label: 'Integration Hub', path: '/ihub', keywords: ['integrations', 'ihub'] },
  { label: 'System (Inbound)', path: '/system', keywords: ['system', 'inbound'] },
];

function matches(dest: Destination, q: string): boolean {
  const s = q.toLowerCase();
  if (dest.label.toLowerCase().includes(s)) return true;
  if (dest.path.toLowerCase().includes(s)) return true;
  if (dest.keywords && dest.keywords.some(k => k.includes(s))) return true;
  return false;
}

export function QuickNavigator() {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey && !e.altKey && e.key.toLowerCase() === 'e') {
        e.preventDefault();
        setOpen(true);
      }
      if (open && e.key === 'Escape') {
        e.preventDefault();
        setOpen(false);
      }
    };
    document.addEventListener('keydown', onKey, { capture: true });
    return () => document.removeEventListener('keydown', onKey, { capture: true } as any);
  }, [open]);

  const filtered = React.useMemo(() => {
    if (!query) return destinations;
    return destinations.filter(d => matches(d, query));
  }, [query]);

  const navigate = (path: string) => {
    if (!path) return;
    if (/^https?:\/\//i.test(path)) {
      window.location.href = path;
      return;
    }
    if (!path.startsWith('/')) {
      path = '/' + path;
    }
    window.location.href = path;
  };

  const handleEnter = () => {
    // If user typed a direct path or URL
    const direct = query.trim();
    if (direct.startsWith('/') || /^https?:\/\//i.test(direct)) {
      navigate(direct);
      setOpen(false);
      setQuery('');
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-xl p-0 border-gray-800 ">
        <Command>
          <CommandInput
            autoFocus
            value={query}
            onValueChange={setQuery}
            placeholder="Go to: customers, sites, ihub, monitoring, /path, or full URL"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                const first = filtered[0];
                if (first && !query.trim().startsWith('/') && !/^https?:\/\//i.test(query.trim())) {
                  navigate(first.path);
                  setOpen(false);
                  setQuery('');
                } else {
                  handleEnter();
                }
              }
            }}
            className="h-12 text-base"
          />
          <CommandList className="max-h-80">
            <CommandEmpty>No matches. Type a /path or full URL and press Enter.</CommandEmpty>
            <CommandGroup heading="Destinations">
              {filtered.map((d) => (
                <CommandItem
                  key={d.path}
                  value={d.label}
                  onSelect={() => {
                    navigate(d.path);
                    setOpen(false);
                    setQuery('');
                  }}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-white">{d.label}</span>
                    <span className="text-xs text-white/60">{d.path}</span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}

export const QuickNavigatorHotkey = QuickNavigator;
